import React from 'react';
import { Link } from 'gatsby';
import SEO from '../../components/Seo';
import { formatDate } from '../../util';
import ContentContainer from '../common/ContentContainer';
import ContentfulContent from '../common/ContentfulContent';
import Layout from '../Layout';
import Author from './../blog/Author';
import './../layout.css';
import './LayoutCommon.css';

type BlogPost = {
  title: string;
  publishedDate: string;
  slug: string;
  author: {
    firstName: string;
    lastName: string;
  };
  featuredImage: {
    fluid: {
      src: string;
    };
  };
  referenceItems: {
    byline: string;
    title: string;
    titleStyle: string;
    trailingNote: string;
  };
};

interface OtherBlogPostProps {
  blogPost: BlogPost;
}

const renderTextWithLinks = (
  text: string | null | undefined
): React.ReactNode => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s)]+|www\.[^\s)]+)/g;
  const elements: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(urlRegex)) {
    const url = match[0];
    const startIndex = match.index ?? 0;
    if (startIndex > lastIndex) {
      elements.push(text.slice(lastIndex, startIndex));
    }
    // Remove a trailing period from the matched URL so links don't include it
    const hasTrailingPeriod = url.endsWith('.');
    const displayUrl = hasTrailingPeriod ? url.slice(0, -1) : url;
    const href = displayUrl.startsWith('http')
      ? displayUrl
      : `https://${displayUrl}`;
    elements.push(
      <a
        key={`${href}-${startIndex}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline hover:text-blue-800"
      >
        {displayUrl}
      </a>
    );
    lastIndex = startIndex + url.length;
  }

  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }

  return <>{elements}</>;
};

const OtherBlogPost: React.FC<OtherBlogPostProps> = props => {
  return (
    <Link to={`/blog/${props.blogPost.slug}`}>
      <div className="bg-white rounded-lg shadow mt-4 p-4 cursor-pointer flex">
        <div className="">
          <h3 className="text-sm text-gray-700 font-semibold">
            {props.blogPost.title}
          </h3>
          <p className="text-xs text-gray-500 uppercase mt-1">
            {props.blogPost.author.firstName} {props.blogPost.author.lastName} |{' '}
            {formatDate(new Date(props.blogPost.publishedDate), true)}
          </p>
        </div>
      </div>
    </Link>
  );
};

interface OtherBlogPostsProps {
  blogPosts: BlogPost[];
}

const OtherBlogPosts: React.FC<OtherBlogPostsProps> = props => {
  return (
    <>
      {props.blogPosts.map((blogPost: BlogPost, i: number) => {
        return <OtherBlogPost key={i} blogPost={blogPost} />;
      })}
    </>
  );
};

interface BlogPostContentProps {
  pageData: any;
}

const BlogPostContent = (props: BlogPostContentProps) => {
  return (
    <>
      {props.pageData?.featuredImage && (
        <img
          className="rounded-t-lg w-full"
          src={props.pageData?.featuredImage?.fluid?.src}
          alt={props.pageData?.title}
        />
      )}
      <section className="bg-white rounded-lg shadow-md p-12">
        <h1 className="font-bold text-3xl text-gray-800">
          {props.pageData?.title}
        </h1>
        <p className="text-gray-700 mt-4">
          Published {formatDate(new Date(props.pageData?.publishedDate))}
        </p>
        <Author
          author={{
            imageSrc: props.pageData?.author?.image?.fluid?.src,
            firstName: props.pageData?.author.firstName,
            lastName: props.pageData?.author?.lastName
          }}
        />
        <section className="mt-10">
          <ContentfulContent content={props.pageData?.body.body} />
          <hr />

          {Array.isArray(props.pageData?.referenceItems) &&
            props.pageData.referenceItems.length > 0 && (
              <div className="mt-4">
                <p className="sans-serif font-bold mb-4">References</p>
                {props.pageData.referenceItems.map(
                  (
                    item: {
                      byline?: string | null;
                      title?: string | null;
                      titleStyle?: string | null;
                      trailingNote?: string | null;
                    },
                    idx: number
                  ) => {
                    const isItalic = item.titleStyle === 'italic';
                    return (
                      <p
                        key={idx}
                        className="text-sm text-gray-500 mb-4"
                        style={{ textIndent: '-1.5rem', paddingLeft: '1.5rem' }}
                      >
                        {renderTextWithLinks(item.byline)}.{` `}
                        {isItalic ? (
                          <span className="italic">
                            {renderTextWithLinks(item.title)}
                          </span>
                        ) : (
                          renderTextWithLinks(item.title)
                        )}
                        . {renderTextWithLinks(item.trailingNote)}.
                      </p>
                    );
                  }
                )}
              </div>
            )}
        </section>
      </section>
    </>
  );
};

const BlogPostLayout: React.FC = ({ pageContext }: any) => {
  return (
    <Layout>
      <SEO title={pageContext?.pageData?.title} />
      <ContentContainer>
        <div className="flex flex-col lg:flex-row">
          <div className="w-full lg:w-2/3 h-full mr-4">
            <BlogPostContent pageData={pageContext?.pageData} />
          </div>
          <div className="w-full lg:w-1/3 h-full ml-0 lg:ml-4 mt-8 lg:mt-0">
            <aside className="px-0 lg:px-8 pt-0">
              <p className="font-bold text-primary sans-serif text-xl">
                Other Blog Posts
              </p>
              <OtherBlogPosts blogPosts={pageContext?.latestBlogPosts} />
            </aside>
          </div>
        </div>
      </ContentContainer>
    </Layout>
  );
};

export default BlogPostLayout;
